import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import { fetchTemplates, addNewTemplate, deleteExistingTemplate, Template } from '../../../../redux/features/doctor/Dashboard/templates/templates';
import {
  Box, Paper, Typography, Button, CircularProgress, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, useMediaQuery, Card, CardContent, CardActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CreateTemplateModal from './components/CreateTemplateModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal'; // Import the new modal

const TemplatesList: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { templates, status } = useAppSelector((state) => state.templates);
  const colors = useAppSelector((state) => state.theme.colors);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');

  // FIX: State to manage the delete confirmation modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);

  useEffect(() => {
    if (status === 'idle') {
      dispatch(fetchTemplates());
    }
  }, [status, dispatch]);

  const handleCreateSubmit = async (data: { title: string; language: string; pastedText?: string }, isFromText: boolean) => {
    setIsSubmitting(true);
    try {
      const resultAction = await dispatch(
        addNewTemplate({
          title: data.title,
          language: data.language,
          pastedText: isFromText ? data.pastedText : undefined,
        })
      );
      
      if (addNewTemplate.fulfilled.match(resultAction)) {
        const newTemplate = resultAction.payload as Template;
        setIsModalOpen(false);
        dispatch(fetchTemplates());
        navigate(`/dashboard/templates/${newTemplate._id}`);
      }
    } catch (err) {
      console.error('Failed to create template:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // FIX: This function now opens the modal instead of showing an alert
  const handleDeleteClick = (templateId: string) => {
    setTemplateToDelete(templateId);
    setIsDeleteModalOpen(true);
  };

  // FIX: This function is called when the user confirms the deletion
  const handleConfirmDelete = () => {
    if (templateToDelete) {
      dispatch(deleteExistingTemplate(templateToDelete));
    }
    setIsDeleteModalOpen(false);
    setTemplateToDelete(null);
  };

  const renderDesktopView = () => (
    <TableContainer sx={{ flexGrow: 1 }}>
      <Table stickyHeader aria-label="templates table">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', color: colors?.listInfo, backgroundColor: colors?.interface, borderBottom: `1px solid ${colors?.folderDataRowBorderColor}` }}>{t('tableTitle')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold', color: colors?.listInfo, backgroundColor: colors?.interface, borderBottom: `1px solid ${colors?.folderDataRowBorderColor}` }}>{t('tableLanguage')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold', color: colors?.listInfo, backgroundColor: colors?.interface, borderBottom: `1px solid ${colors?.folderDataRowBorderColor}` }}>{t('tableCategory')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold', color: colors?.listInfo, backgroundColor: colors?.interface, borderBottom: `1px solid ${colors?.folderDataRowBorderColor}` }}>{t('tableLastModified')}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', color: colors?.listInfo, backgroundColor: colors?.interface, borderBottom: `1px solid ${colors?.folderDataRowBorderColor}` }}>{t('tableActions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {templates.map((template) => (
            <TableRow key={template._id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { backgroundColor: colors?.sectionHover } }}>
              <TableCell onClick={() => navigate(`/dashboard/templates/${template._id}`)} sx={{ color: colors?.text, cursor: 'pointer', borderBottom: `1px solid ${colors?.folderDataRowBorderColor}` }}>{template.title}</TableCell>
              <TableCell onClick={() => navigate(`/dashboard/templates/${template._id}`)} sx={{ color: colors?.text, cursor: 'pointer', borderBottom: `1px solid ${colors?.folderDataRowBorderColor}` }}>{template.language}</TableCell>
              <TableCell onClick={() => navigate(`/dashboard/templates/${template._id}`)} sx={{ color: colors?.text, cursor: 'pointer', borderBottom: `1px solid ${colors?.folderDataRowBorderColor}` }}>{template.category || 'General'}</TableCell>
              <TableCell onClick={() => navigate(`/dashboard/templates/${template._id}`)} sx={{ color: colors?.text, cursor: 'pointer', borderBottom: `1px solid ${colors?.folderDataRowBorderColor}` }}>
                {new Date(template.updatedAt || template.createdAt || Date.now()).toLocaleDateString()}
              </TableCell>
              <TableCell align="right" sx={{ borderBottom: `1px solid ${colors?.folderDataRowBorderColor}` }}>
                <IconButton onClick={() => navigate(`/dashboard/templates/${template._id}`)} sx={{ color: colors?.listInfo, '&:hover': { color: colors?.lightBlue } }}>
                  <EditIcon />
                </IconButton>
                {/* FIX: Call handleDeleteClick */}
                <IconButton onClick={() => handleDeleteClick(template._id)} sx={{ color: colors?.listInfo, '&:hover': { color: colors?.red } }}>
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderMobileView = () => (
    <Box sx={{ overflowY: 'auto', flexGrow: 1, p: 1 }}>
      {templates.map((template) => (
        <Card 
          key={template._id} 
          sx={{ 
            mb: 2, 
            backgroundColor: colors?.interface, 
            border: `1px solid ${colors?.folderDataRowBorderColor}`,
            borderRadius: 3,
            boxShadow: 'none'
          }}
        >
          <CardContent onClick={() => navigate(`/dashboard/templates/${template._id}`)} sx={{ pb: '8px !important' }}>
            <Typography variant="h6" component="div" sx={{ color: colors?.text, fontWeight: 'bold' }}>
              {template.title}
            </Typography>
            <Typography sx={{ mb: 1.5, color: colors?.listInfo, fontSize: '0.9rem' }}>
              {template.category || 'General'}
            </Typography>
            <Typography variant="body2" color={colors?.text}>
              {t('language')}: {template.language}
            </Typography>
          </CardContent>
          <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
            <IconButton onClick={() => navigate(`/dashboard/templates/${template._id}`)} sx={{ color: colors?.listInfo, '&:hover': { color: colors?.lightBlue } }}>
              <EditIcon />
            </IconButton>
            {/* FIX: Call handleDeleteClick */}
            <IconButton onClick={() => handleDeleteClick(template._id)} sx={{ color: colors?.listInfo, '&:hover': { color: colors?.red } }}>
              <DeleteIcon />
            </IconButton>
          </CardActions>
        </Card>
      ))}
    </Box>
  );

  return (
    <>
      <CreateTemplateModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreateSubmit}
        isSubmitting={isSubmitting}
      />

      {/* FIX: Add the delete modal to the component render */}
      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t('deleteTemplateTitle')}
        message={t('deleteTemplateWarning')}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: isMobile ? 2 : 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" sx={{ color: colors?.text, fontWeight: 'bold' }}>
            {t('myTemplates')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsModalOpen(true)}
            size={isMobile ? 'medium' : 'large'}
            sx={{ 
              backgroundColor: colors?.lightBlue, 
              '&:hover': { backgroundColor: colors?.darkBlue }, 
              fontWeight: 'bold', 
              borderRadius: 2, 
              px: isMobile ? 2 : 3, 
              py: isMobile ? 1 : 1.5,
              boxShadow: `0 4px 12px ${colors?.avatar}`
            }}
          >
            {isMobile ? t('create') : t('createNewTemplate')}
          </Button>
        </Box>

        <Paper
          elevation={0}
          sx={{
            flexGrow: 1,
            backgroundColor: colors?.container,
            borderRadius: 4,
            border: `1px solid ${colors?.folderDataRowBorderColor}`,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {status === 'loading' && !templates.length ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress sx={{ color: colors?.lightBlue }} /></Box>
          ) : (
            isMobile ? renderMobileView() : renderDesktopView()
          )}
          {status !== 'loading' && templates.length === 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography sx={{ p: 4, textAlign: 'center', color: colors?.listInfo }}>
                      {t('noTemplatesMessage')}
                  </Typography>
              </Box>
          )}
        </Paper>
      </Box>
    </>
  );
};

export default TemplatesList;